import "lazysizes";
import "lazysizes/plugins/attrchange/ls.attrchange";
import { loadableReady } from "@loadable/component";
import { Provider } from "mobx-react";
import ReactDOM from "react-dom";
import { HelmetProvider } from "react-helmet-async";
import { Route, Router } from "react-router-dom";

import { StartupEventMap } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { isIHRSession, isPWSSession } from "@inferno/renderer-shared-core";

import { App } from "../app/sites/App.component";
import { UspApiFunc } from "../app/core/lib/iab";
import { performance } from "./helpers";
import {
  initABTestGroups,
  initIntegrations,
  initLocale,
  initSession,
  bootstrap,
  initLazySizes,
  loadIntegrationScripts,
} from "./setup";
import { initAppsFlyer } from "./appsflyer-init";
import { withTimeout } from "../app/core/lib/promise-timeout";

const log = ILog.logger("client/index.tsx");

declare global {
  interface Window {
    __PRELOADED_STATE__: any;
    bootEvents: StartupEventMap;
    moatYieldReady: () => void;
    domCompleteReady: () => void;
    bridgeReady: () => void;
    lazySizesConfig: {
      preloadAfterLoad: boolean;
      init: boolean;
    };
    lazySizes: {
      init: () => void;
      cfg: any;
    };
    recaptchaCallback: (result: string) => void;
    invokeAnalyticsVendorBeacons?: (opts: { ccpa: boolean }) => void;
    deferAnalyticsVendorBeacons?: boolean;
    __uspapi?: UspApiFunc;
    newrelic: typeof import("newrelic");
  }
}

loadableReady(async () => {
  performance("mark", ["Inferno - Loadable Ready"]);

  const { store, browserHistory, no3rdParty } = bootstrap();

  initSession(store, no3rdParty);

  await initABTestGroups(store, no3rdParty);

  await initLocale(store);

  if (!no3rdParty && store.site.isTalkbackEnabled) {
    initAppsFlyer(store);
  }

  loadIntegrationScripts(store);

  log.debug("Hydrating App Start (Synchronously)");
  ReactDOM.hydrate(
    <HelmetProvider>
      <Provider store={store}>
        <Router history={browserHistory}>
          <Route component={App} />
        </Router>
      </Provider>
    </HelmetProvider>,
    document.getElementById("app"),
    async () => {
      log.debug("Hydrating App Finished", "(React will now queue useEffect hooks)");

      initLazySizes();

      // Shim fetch to use keepalive for privacy API POST requests.
      // OneTrust's BannerRefreshAfterCallToActionEnabled reloads the page on consent change,
      // which aborts the SDK's in-flight POST to update AMP privacy settings.
      // keepalive: true ensures the request completes even during page navigation.
      const originalFetch = window.fetch.bind(window);
      window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
        if (url.includes("/api/v3/privacy/requests")) {
          init = { ...init, keepalive: true };
        }
        return originalFetch(input, init);
      }) as typeof window.fetch;

      try {
        if (store.privacy.enabled) {
          log.debug("Initializing Privacy SDK");
          const sessionState = store.session.currentSession?.state;
          const pwsSession = isPWSSession(sessionState);
          const ihrSession = isIHRSession(sessionState) ? sessionState : undefined;

          const privacyTimeout = 6000;
          await withTimeout(
            store.privacy.initialize({
              host: store.env.ONETRUST_HOST,
              iglooUrl: store.env.IGLOO_URL || "",
              ...(pwsSession && {
                oneTrustId: store.env.ONETRUST_COAST_ID,
                autoBlock: true,
              }),
              ...(ihrSession && {
                ampConfig: {
                  profileId: String(ihrSession.profile?.profileId ?? ihrSession.pid),
                  sessionId: ihrSession.profile?.sessionId ?? ihrSession.aid,
                  hostName: window.location.hostname,
                },
                user: {
                  profileId: ihrSession.profile?.profileId ?? 0,
                  name: ihrSession.profile?.name,
                  email: ihrSession.profile?.email,
                },
              }),
            }),
            privacyTimeout,
            `Privacy SDK initialization timed out after ${privacyTimeout / 1000} seconds`,
          );
          log.debug("Privacy SDK initialized");
        } else {
          log.debug("Privacy SDK disabled for this site (onetrust_switch is off)");
        }
      } catch (err) {
        window.newrelic?.noticeError(err as Error);
      } finally {
        initIntegrations(store, no3rdParty);
        store.trackPageViews();
      }
      log.debug(
        "Loadable Ready Finished - Event Loop Callback/MicroTasks Queue from Integrations Callback Starts Running",
      );
    },
  );
});
